import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { outletService } from '../../services/outletService';
import { OutletListDTO } from '../../types';
import { CirclePlus, Eye, RefreshCw, SquarePen, Store, CheckCircle2 } from 'lucide-react';
import '../../modules/restaurant/pages/RestaurantPage.css';

const OutletListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<OutletListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unwrap = (res: any): OutletListDTO[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await outletService.getAll();
      setItems(unwrap(res));
    } catch {
      setError(t('restaurant.outlet.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await outletService.getAll();
      setItems(unwrap(res));
    } catch {
      setError(t('restaurant.outlet.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const activeCount = items.filter((i) => i.isActive).length;

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('restaurant.outlet.code'),
      sortable: true,
      width: '110px',
      render: (value) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ims-raw-orange-700)' }}>
          {value}
        </span>
      ),
    },
    {
      key: 'name',
      label: t('restaurant.outlet.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'warehouseName',
      label: t('restaurant.outlet.warehouse'),
      width: '160px',
      hideTablet: true,
      render: (value) => <span>{value || '—'}</span>,
    },
    {
      key: 'address',
      label: t('restaurant.outlet.address'),
      width: '220px',
      hideMobile: true,
      hideTablet: true,
      render: (value) => <span>{value || '—'}</span>,
    },
    {
      key: 'phone',
      label: t('restaurant.outlet.phone'),
      width: '140px',
      hideMobile: true,
      render: (value) => <span>{value || '—'}</span>,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '110px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'rest-badge-active' : 'rest-badge-inactive'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/outlets/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/restaurant/outlets/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
  ];

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('restaurant.outlet.title')}</h1>
            <p className="ims-list-subtitle">{t('common.manageAndOrganize', 'Manage your restaurant outlets and branches')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/outlets/create')}>
              <CirclePlus size={16} strokeWidth={1.75} />
              {t('restaurant.outlet.newOutlet')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-orange">
              <Store size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Outlets</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-green">
              <CheckCircle2 size={20} strokeWidth={1.5} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">Active Outlets</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid
            data={items}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('restaurant.outlet.noOutletsFound')}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default OutletListPage;
